/**
 * Кастомные критерии (#857) и проверка формулировки на последних звонках (#861).
 *
 * Каждый ответ разбирается схемой из `@aica/api-types`: форма ответа решает, что
 * человек увидит (счётчик лимита, статус, цена проверки), и молча принять чужую
 * форму нельзя.
 */

import {
  CRITERIA_ASSIST_PROXY_DEADLINE_MS,
  CRITERIA_TRIAL_PROXY_DEADLINE_MS,
  criteriaActivityResponseSchema,
  criteriaDraftResponseSchema,
  criteriaReviseResponseSchema,
  criteriaTrialResponseSchema,
  getCallCriterionResponseSchema,
  listCallCriteriaResponseSchema,
  type CallCriterionResponse,
  type CriteriaActivityResponse,
  type CreateCallCriterionRequest,
  type CriteriaDraftRequest,
  type CriteriaDraftResponse,
  type CriteriaReviseRequest,
  type CriteriaReviseResponse,
  type CriteriaTrialRequest,
  type CriteriaTrialResponse,
  type ListCallCriteriaResponse,
  type UpdateCallCriterionRequest,
} from '@aica/api-types/schemas';

import { BaseApiClient } from '../base';
import { API_ENDPOINTS } from '../endpoints';

/**
 * Сколько клиент ждёт проверку. Чуть дольше, чем edge-api ждёт workflows-api:
 * edge сам отвечает кодом `trial_timeout` на своём пределе, и клиенту нужно
 * успеть прочитать этот ответ, а не оборвать запрос раньше и показать «сбой
 * сети» там, где вызовы уже оплачены.
 */
export const CRITERIA_TRIAL_CLIENT_DEADLINE_MS =
  CRITERIA_TRIAL_PROXY_DEADLINE_MS + 10_000;

/** То же для помощника и правки: edge отвечает `assist_timeout` на своём пределе. */
export const CRITERIA_ASSIST_CLIENT_DEADLINE_MS =
  CRITERIA_ASSIST_PROXY_DEADLINE_MS + 10_000;

export class CallCriteriaApi extends BaseApiClient {
  list(): Promise<ListCallCriteriaResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.CALL_CRITERIA,
      listCallCriteriaResponseSchema
    );
  }

  /** Как отвечали критерии за 7 дней (#861, PR 4): только org-wide роли. */
  activity(): Promise<CriteriaActivityResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.CALL_CRITERIA_ACTIVITY,
      criteriaActivityResponseSchema
    );
  }

  /** Свежая строка — база формы правки: список в кеше бывает старше. */
  async get(id: string): Promise<CallCriterionResponse> {
    const result = await this.validatedRequest(
      API_ENDPOINTS.CALL_CRITERIA_BY_ID(id),
      getCallCriterionResponseSchema
    );
    return result.criterion;
  }

  async create(body: CreateCallCriterionRequest): Promise<CallCriterionResponse> {
    const result = await this.validatedRequest(
      API_ENDPOINTS.CALL_CRITERIA,
      getCallCriterionResponseSchema,
      { method: 'POST', body: JSON.stringify(body) }
    );
    return result.criterion;
  }

  /** Правка, пауза (`isActive`) и область — одним PATCH. */
  async update(
    id: string,
    body: UpdateCallCriterionRequest
  ): Promise<CallCriterionResponse> {
    const result = await this.validatedRequest(
      API_ENDPOINTS.CALL_CRITERIA_BY_ID(id),
      getCallCriterionResponseSchema,
      { method: 'PATCH', body: JSON.stringify(body) }
    );
    return result.criterion;
  }

  async archive(id: string): Promise<CallCriterionResponse> {
    const result = await this.validatedRequest(
      API_ENDPOINTS.CALL_CRITERIA_ARCHIVE(id),
      getCallCriterionResponseSchema,
      { method: 'POST' }
    );
    return result.criterion;
  }

  /**
   * Проверка черновика на последних звонках. Платная, поэтому БЕЗ повторов:
   * повтор после 503 — это новое нажатие, до двадцати вызовов провайдера и
   * ещё один слот суточного потолка организации. Сам fetch POST без
   * `Idempotency-Key` не повторяет, но `maxRetries: 0` делает это явным, а не
   * следствием чужого правила. Предел ожидания — свой: ответ идёт до ~95 с.
   */
  trial(body: CriteriaTrialRequest): Promise<CriteriaTrialResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.CALL_CRITERIA_TRIAL,
      criteriaTrialResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify(body),
        signal: AbortSignal.timeout(CRITERIA_TRIAL_CLIENT_DEADLINE_MS),
      },
      0
    );
  }

  /**
   * Черновик критерия из описания своими словами (#1416). Платный вызов модели,
   * поэтому без повторов — по тем же причинам, что у `trial`.
   */
  draft(body: CriteriaDraftRequest): Promise<CriteriaDraftResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.CALL_CRITERIA_DRAFT,
      criteriaDraftResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify(body),
        signal: AbortSignal.timeout(CRITERIA_ASSIST_CLIENT_DEADLINE_MS),
      },
      0
    );
  }

  /** Правка формулировки по отмеченным неверным ответам (#1416): тоже платная, без повторов. */
  revise(body: CriteriaReviseRequest): Promise<CriteriaReviseResponse> {
    return this.validatedRequest(
      API_ENDPOINTS.CALL_CRITERIA_REVISE,
      criteriaReviseResponseSchema,
      {
        method: 'POST',
        body: JSON.stringify(body),
        signal: AbortSignal.timeout(CRITERIA_ASSIST_CLIENT_DEADLINE_MS),
      },
      0
    );
  }
}
